import { useEffect, useRef } from 'react';

// Service Worker注册和更新管理
export const useServiceWorkerRegistration = () => {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const updateCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const updateCallbacksRef = useRef<Array<(hasUpdate: boolean) => void>>([]);

  useEffect(() => {
    // 只在客户端和HTTPS环境下注册
    if (typeof window === 'undefined' || !window.isSecureContext) {
      return;
    }

    // 检查浏览器支持
    if (!('serviceWorker' in navigator)) {
      return;
    }

    // 注册更新回调
    const onUpdate = (callback: (hasUpdate: boolean) => void) => {
      updateCallbacksRef.current.push(callback);
      return () => {
        updateCallbacksRef.current = updateCallbacksRef.current.filter(cb => cb !== callback);
      };
    };

    // 检查更新
    const checkForUpdate = async () => {
      if (!registrationRef.current) return;
      
      try {
        // 强制检查更新，不使用缓存
        await registrationRef.current.update();
      } catch (error) {
        console.warn('[SW] 检查更新失败:', error);
      }
    };

    // 安全地注册Service Worker
    const registerServiceWorker = async () => {
      try {
        // 先尝试获取现有注册
        let registration = await navigator.serviceWorker.getRegistration();
        
        if (!registration) {
          // 如果没有现有注册，注册Service Worker
          registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/',
            updateViaCache: 'none', // 每次都检查更新，不使用 HTTP 缓存
          });
          console.log('[SW] Service Worker 注册成功');
        } else {
          console.log('[SW] 使用现有Service Worker注册');
          // 即使已有注册，也立即检查更新
          try {
            await registration.update();
          } catch (updateError) {
            console.warn('[SW] 检查更新时出错:', updateError);
          }
        }

        registrationRef.current = registration;

        // 监听更新
        registration?.addEventListener('updatefound', () => {
          const newWorker = registration?.installing || registration?.waiting;
          if (newWorker) {
            console.log('[SW] 检测到新版本，正在安装...');
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // 有现有控制器，说明这是更新
                  console.log('[SW] 新Service Worker已安装，通知用户更新');
                  
                  // 通知所有注册的回调
                  updateCallbacksRef.current.forEach(callback => {
                    try {
                      callback(true);
                    } catch (error) {
                      console.error('[SW] 更新回调执行失败:', error);
                    }
                  });

                  // 尝试立即激活新版本（跳过等待）
                  if (newWorker.state === 'installed' && registration?.waiting) {
                    // 发送 skipWaiting 消息
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                  }
                } else {
                  // 没有现有控制器，说明这是首次安装
                  console.log('[SW] Service Worker 首次安装完成');
                }
              } else if (newWorker.state === 'activated') {
                console.log('[SW] 新Service Worker已激活');
                // 新版本激活后，通知页面刷新
                updateCallbacksRef.current.forEach(callback => {
                  try {
                    callback(true);
                  } catch (error) {
                    console.error('[SW] 激活回调执行失败:', error);
                  }
                });
              }
            });
          }
        });

        // 监听 Service Worker 控制权变化
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[SW] Service Worker 控制权已变更，页面需要刷新');
          // 通知所有注册的回调
          updateCallbacksRef.current.forEach(callback => {
            try {
              callback(true);
            } catch (error) {
              console.error('[SW] 控制权变更回调执行失败:', error);
            }
          });
        });

        // 立即检查一次更新
        checkForUpdate();

        // 定期检查更新（每30秒）
        updateCheckIntervalRef.current = setInterval(() => {
          checkForUpdate();
        }, 30000);

        // 页面可见时检查更新
        const handleVisibilityChange = () => {
          if (!document.hidden) {
            checkForUpdate();
          }
        };

        // 页面获得焦点时检查更新
        const handleFocus = () => {
          checkForUpdate();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // 监听 Service Worker 消息
        const handleMessage = (event: MessageEvent) => {
          if (event.data && event.data.type === 'SKIP_WAITING') {
            console.log('[SW] 收到 skipWaiting 确认');
          }
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        // 暴露更新检查方法到全局（供其他组件使用）
        (window as any).__swUpdateCheck = checkForUpdate;
        (window as any).__swOnUpdate = onUpdate;

        return () => {
          if (updateCheckIntervalRef.current) {
            clearInterval(updateCheckIntervalRef.current);
          }
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('focus', handleFocus);
          navigator.serviceWorker.removeEventListener('message', handleMessage);
          delete (window as any).__swUpdateCheck;
          delete (window as any).__swOnUpdate;
        };
      } catch (error) {
        console.error('[SW] Service Worker注册失败:', error);
      }
    };

    // 延迟注册，确保页面完全加载
    const timeoutId = setTimeout(registerServiceWorker, 1000);

    return () => {
      clearTimeout(timeoutId);
      if (updateCheckIntervalRef.current) {
        clearInterval(updateCheckIntervalRef.current);
      }
    };
  }, []);

  return {
    checkForUpdate: () => {
      if ((window as any).__swUpdateCheck) {
        (window as any).__swUpdateCheck();
      }
    },
    onUpdate: (callback: (hasUpdate: boolean) => void) => {
      if ((window as any).__swOnUpdate) {
        return (window as any).__swOnUpdate(callback);
      }
      return () => {};
    },
  };
};

